import type { LedgerEvent } from '@talos/kyoko';
import {
  createContext,
  useContext,
  useMemo,
  useState,
  type Dispatch,
  type PropsWithChildren,
  type SetStateAction,
} from 'react';

export const ReconDetailsInteractionsContext = createContext<ReconDetailsInteractionsContextProps | undefined>(
  undefined
);

export type ReconDetailsInteractionsContextProps = {
  zoomOutChart: (() => void) | undefined;
  setZoomOutChart: Dispatch<SetStateAction<(() => void) | undefined>>;
  highlightRows: ((ledgerEvents: LedgerEvent[]) => void) | undefined;
  setHighlightRows: Dispatch<SetStateAction<((ledgerEvents: LedgerEvent[]) => void) | undefined>>;
};

export function useReconDetailsInteractions() {
  const context = useContext(ReconDetailsInteractionsContext);
  if (context === undefined) {
    throw new Error(
      'Missing ReconDetailsInteractionsContext.Provider further up in the tree. Did you forget to add it?'
    );
  }
  return context;
}

// This provider holds a few functions at the top of the page which any child can use in order to interact with different elements of the page
export const ReconDetailsInteractionsProvider = function ReconDetailsInteractionsProvider({
  children,
}: PropsWithChildren) {
  const [zoomOutChart, setZoomOutChart] = useState<(() => void) | undefined>(undefined);
  const [highlightRows, setHighlightRows] = useState<((ledgerEvents: LedgerEvent[]) => void) | undefined>(undefined);

  const value = useMemo(() => {
    return {
      zoomOutChart,
      setZoomOutChart,
      highlightRows,
      setHighlightRows,
    };
  }, [zoomOutChart, highlightRows]);

  return <ReconDetailsInteractionsContext.Provider value={value}>{children}</ReconDetailsInteractionsContext.Provider>;
};
